import { useEffect, useState } from 'react';
import type { CollectionQuery, CollectionWithQuery } from '@mlc/shared';
import { formatCourse, formatCuisine } from '@mlc/shared';
import { deleteCollection, listCollections, reorderCollections, updateCollection } from './api';
import { Link, paths, withSearchParams } from './router';
import { Banner, Button, EmptyState, Icon, Loading, PageHeader, TextInput, buttonClasses } from './ui';

/**
 * Les collections retrouvent un écran (US-63).
 *
 * ### Ce qu'elles étaient
 *
 * Une collection est une **recherche sauvegardée** (US-21) — « un dossier se
 * périme dès qu'une nouvelle recette arrive, une recherche non ». La décision
 * était juste ; c'est son exécution qui manquait, et l'inventaire fait mal :
 *
 * - **aucun écran**, aucune entrée de navigation : elles n'existaient que dans
 *   un groupe replié de la barre de filtres ;
 * - **`updateCollection` n'avait aucun appelant** : on ne pouvait ni renommer
 *   une collection ni modifier ses critères ;
 * - **`position` n'était modifiable par aucune route**, alors que le DDL la
 *   décrit comme « l'ordre d'affichage, choisi par l'utilisateur » ;
 * - trois critères seulement (`q`, `tags`, `status`), sans donnée de catégorie
 *   à interroger : « desserts » dépendait d'une étiquette posée à la main sur
 *   chaque recette. US-55 a apporté la matière.
 *
 * ### Ce que cet écran fait, et ne fait pas
 *
 * Il **montre**, **range**, **renomme** et **ouvre**. Il ne compose pas de
 * critères : on enregistre une collection depuis la barre de filtres, là où
 * l'on vient précisément de les choisir et où on en voit le résultat. Un
 * second éditeur de critères ici aurait été un formulaire de recherche
 * déguisé, à côté d'une recherche qui marche.
 *
 * Le compte affiché vient du serveur, **dans le périmètre visible** : une
 * collection est une requête, son compte en est le résultat, et le calculer
 * ici dirait ce que la clause de visibilité vient de cacher.
 */

/** Les critères d'une collection, en français. Une collection sans critère est la bibliothèque entière — et le dit. */
function decrire(query: CollectionQuery): string {
  const morceaux: string[] = [];
  if (query.q !== undefined && query.q !== '') morceaux.push(`« ${query.q} »`);
  if (query.status === 'a_cuisiner') morceaux.push('à cuisiner');
  if (query.status === 'favorite') morceaux.push('favoris');
  if (query.course !== undefined) morceaux.push(formatCourse(query.course) ?? query.course);
  if (query.cuisine !== undefined) morceaux.push(formatCuisine(query.cuisine) ?? query.cuisine);
  for (const tag of query.tags ?? []) morceaux.push(tag);
  return morceaux.length === 0 ? 'toutes les recettes' : morceaux.join(' · ');
}

/** L'adresse de la bibliothèque filtrée par cette collection — la collection *est* ce lien. */
function lienVersLaBibliotheque(query: CollectionQuery): string {
  return withSearchParams(paths.library, {
    q: query.q ?? '',
    statut: query.status ?? '',
    etiquettes: (query.tags ?? []).join(','),
    type: query.course ?? '',
    cuisine: query.cuisine ?? '',
  });
}

export function Collections(): React.JSX.Element {
  const [collections, setCollections] = useState<CollectionWithQuery[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [renommage, setRenommage] = useState<{ id: string; nom: string } | null>(null);
  const [aSupprimer, setASupprimer] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    listCollections(controller.signal)
      .then(setCollections)
      .catch((e: unknown) => {
        if (e instanceof Error && e.name !== 'AbortError') setErreur(e.message);
      });
    return () => { controller.abort(); };
  }, []);

  function renommer(collection: CollectionWithQuery, nom: string): void {
    const propre = nom.trim();
    if (propre === '' || propre === collection.name) { setRenommage(null); return; }
    setErreur(null);
    updateCollection(collection.id, { name: propre, query: collection.query })
      .then((modifiee) => {
        setCollections((actuelles) =>
          actuelles?.map((c) => (c.id === modifiee.id ? { ...c, name: modifiee.name } : c)) ?? null);
        setRenommage(null);
      })
      .catch((e: unknown) => { setErreur(e instanceof Error ? e.message : String(e)); });
  }

  function deplacer(index: number, sens: -1 | 1): void {
    if (collections === null) return;
    const cible = index + sens;
    if (cible < 0 || cible >= collections.length) return;
    const ordonnees = [...collections];
    const [deplacee] = ordonnees.splice(index, 1);
    if (deplacee === undefined) return;
    ordonnees.splice(cible, 0, deplacee);
    // Optimiste : l'ordre est un confort, et le serveur renvoie la vérité.
    setCollections(ordonnees);
    reorderCollections(ordonnees.map((c) => c.id))
      .then(setCollections)
      .catch((e: unknown) => { setErreur(e instanceof Error ? e.message : String(e)); });
  }

  function supprimer(collection: CollectionWithQuery): void {
    setErreur(null);
    deleteCollection(collection.id)
      .then(() => {
        setCollections((actuelles) => actuelles?.filter((c) => c.id !== collection.id) ?? null);
        setASupprimer(null);
      })
      .catch((e: unknown) => { setErreur(e instanceof Error ? e.message : String(e)); });
  }

  return (
    <section aria-labelledby="titre-collections" className="flex flex-col gap-6">
      {/* Les collections sont une façon de lire la bibliothèque (US-63), donc
          un écran **enfant** : le lien de retour a ici un sens, contrairement
          aux onglets de premier niveau d'où il a été retiré. */}
      <PageHeader
        title={<span id="titre-collections">Mes collections</span>}
        lede="Une collection est une recherche enregistrée : elle reste juste quand une nouvelle recette arrive. Elles s’enregistrent depuis les filtres de la bibliothèque."
        back={<Link to={paths.library} className={buttonClasses('quiet', 'compact')}><Icon name="fleche-gauche" size={0.9} /> Bibliothèque</Link>}
      />

      {erreur !== null && <Banner tone="danger">{erreur}</Banner>}

      {collections === null ? (
        <Loading label="vos collections" lines={2} />
      ) : collections.length === 0 ? (
        <div className="max-w-reading">
          <EmptyState
            title="Aucune collection pour l’instant"
            description="Choisissez des filtres dans la bibliothèque, puis enregistrez-les : « desserts italiens », « moins de 30 minutes »."
            // Un état vide sans issue est une impasse (`ui/EmptyState.tsx`) :
            // celui-ci expliquait où aller sans y conduire.
            action={<Link to={paths.library} className={buttonClasses('primary')}>Aller aux filtres</Link>}
          />
        </div>
      ) : (
        // `max-w-reading` : une collection est une ligne de texte suivie de
        // deux commandes. Étirée sur 1300 px, le nom se retrouvait à un bout
        // et « Renommer » à l'autre.
        <ul data-testid="collections" className="flex max-w-reading flex-col gap-3">
          {collections.map((collection, index) => (
            <li
              key={collection.id}
              className="flex flex-col gap-2 rounded-card border border-border p-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                {renommage?.id === collection.id ? (
                  <>
                    <label htmlFor={`collection-nom-${collection.id}`} className="sr-only">
                      Nouveau nom de la collection {collection.name}
                    </label>
                    <TextInput
                      id={`collection-nom-${collection.id}`}
                      value={renommage.nom}
                      autoFocus
                      onChange={(e) => { setRenommage({ id: collection.id, nom: e.target.value }); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') renommer(collection, renommage.nom); }}
                      className="w-56"
                    />
                    <Button type="button" variant="secondary" size="compact" onClick={() => { renommer(collection, renommage.nom); }}>
                      Renommer
                    </Button>
                    <Button type="button" variant="ghost" size="compact" onClick={() => { setRenommage(null); }}>
                      Annuler
                    </Button>
                  </>
                ) : (
                  <>
                    {/* La collection **est** ce lien : l'ouvrir, c'est rejouer
                        sa recherche dans la bibliothèque. */}
                    <Link
                      to={lienVersLaBibliotheque(collection.query)}
                      className={buttonClasses('primary', 'compact')}
                    >
                      {collection.name}
                    </Link>
                    {collection.recipe_count !== undefined && (
                      <span className="text-sm text-foreground-muted">
                        <span className="text-data">{collection.recipe_count}</span>
                        {collection.recipe_count > 1 ? ' recettes' : ' recette'}
                      </span>
                    )}
                    <span className="flex-1" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="compact"
                      aria-label={`Monter ${collection.name}`}
                      disabled={index === 0}
                      onClick={() => { deplacer(index, -1); }}
                    >
                      Monter
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="compact"
                      aria-label={`Descendre ${collection.name}`}
                      disabled={index === collections.length - 1}
                      onClick={() => { deplacer(index, 1); }}
                    >
                      Descendre
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="compact"
                      onClick={() => { setRenommage({ id: collection.id, nom: collection.name }); }}
                    >
                      Renommer
                    </Button>
                    {aSupprimer === collection.id ? (
                      <>
                        <span className="text-sm text-foreground-secondary">Supprimer ce rangement ?</span>
                        <Button type="button" variant="secondary" size="compact" onClick={() => { supprimer(collection); }}>
                          Confirmer
                        </Button>
                        <Button type="button" variant="ghost" size="compact" onClick={() => { setASupprimer(null); }}>
                          Annuler
                        </Button>
                      </>
                    ) : (
                      <Button
                        type="button"
                        variant="secondary"
                        size="compact"
                        onClick={() => { setASupprimer(collection.id); }}
                      >
                        Supprimer
                      </Button>
                    )}
                  </>
                )}
              </div>
              <p className="text-sm text-foreground-secondary">{decrire(collection.query)}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
